import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BugReportIcon from '@mui/icons-material/BugReport';
import { ToolCallEntry } from '../../shared/interfaces';

const TOOL_LABELS: Record<string, string> = {
  web_search: 'Web Search',
  page_scraping: 'Page Scraping',
  data_ocean_query: 'Data Ocean Query',
  skill_read: 'Leitura de skill',
  erp_query: 'ERP Query',
  erp_create_invoice: 'ERP Criar NF',
  datajud_query: 'DataJud Query',
  code_exec_bash: 'Code Exec (bash)',
  code_exec_write: 'Code Exec (write)',
  code_exec_edit: 'Code Exec (edit)',
  code_exec_read: 'Code Exec (read)',
  code_exec_show: 'Code Exec (show)',
  buscar_precedentes: 'Busca Precedentes',
  message_composer: 'Message Composer',
};

const Container = styled.div`
  background: ${({ theme }) => theme.input_surface_bg || '#f8f9fa'};
  border: 1px solid #ff980050;
  border-radius: 12px;
  padding: 12px 16px;
  margin: 8px 0;
  max-width: 700px;
  font-family: 'Sora', 'Lexend', sans-serif;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  color: ${({ theme }) => theme.font_color || '#333'};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const Title = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: #ff9800;
`;

const Badge = styled.span`
  font-size: 0.7rem;
  background: #ff980030;
  color: #ff9800;
  padding: 1px 6px;
  border-radius: 8px;
  font-weight: 600;
`;

const ExpandIconWrapper = styled.div`
  opacity: 0.5;
  display: flex;
  align-items: center;
`;

const CollapsibleContent = styled.div<{ $expanded: boolean }>`
  overflow: hidden;
  max-height: ${({ $expanded }) => $expanded ? 'none' : '0'};
  opacity: ${({ $expanded }) => $expanded ? 1 : 0};
  transition: opacity 0.2s ease;
  margin-top: ${({ $expanded }) => $expanded ? '8px' : '0'};
`;

const ToolCallList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ToolCallItem = styled.div`
  border: 1px solid ${({ theme }) => theme.borderGrey || '#e0e0e0'};
  border-radius: 8px;
  overflow: hidden;
`;

const ToolCallHeader = styled.div<{ $hasResult: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.font_color || '#333'};
  background: ${({ theme }) => theme.input_surface_bg || '#f5f5f5'};
  &:hover {
    opacity: 0.8;
  }
`;

const ToolName = styled.span`
  font-weight: 600;
  font-size: 0.78rem;
`;

const ToolArgsSummary = styled.span`
  font-size: 0.72rem;
  opacity: 0.6;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 12px;
  height: 12px;
  border: 2px solid ${({ theme }) => theme.borderGrey || '#e0e0e0'};
  border-top-color: #ff9800;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  flex-shrink: 0;
`;

const ResultDot = styled.div<{ $error?: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $error }) => $error ? '#f44336' : '#4caf50'};
  flex-shrink: 0;
`;

const ToolCallBody = styled.div`
  padding: 8px 10px;
  border-top: 1px solid ${({ theme }) => theme.borderGrey || '#e0e0e0'};
`;

const SectionLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  opacity: 0.5;
  margin-bottom: 4px;
  color: ${({ theme }) => theme.font_color || '#666'};
`;

const CodeBlock = styled.pre`
  font-family: 'Fira Code', 'Consolas', monospace;
  font-size: 0.72rem;
  line-height: 1.4;
  background: ${({ theme }) => (theme as any).code_bg || '#1e1e1e'};
  color: ${({ theme }) => (theme as any).code_color || '#d4d4d4'};
  padding: 8px 10px;
  border-radius: 6px;
  overflow-x: auto;
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
`;

const TruncatedBadge = styled.span`
  font-size: 0.65rem;
  color: #ff9800;
  margin-left: 4px;
`;

interface Props {
  toolCalls: ToolCallEntry[];
  isStreaming: boolean;
}

function getArgsSummary(args: Record<string, any>): string {
  return JSON.stringify(args).substring(0, 2000);
}

const ToolCallsDebugPanel: React.FC<Props> = ({ toolCalls, isStreaming }) => {
  const [expanded, setExpanded] = useState(true);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const toggleItem = (idx: number) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  if (toolCalls.length === 0) return null;

  return (
    <Container>
      <Header onClick={() => setExpanded(!expanded)}>
        <HeaderLeft>
          <BugReportIcon style={{ fontSize: 18, color: '#ff9800' }} />
          <Title>Tool Calls</Title>
          <Badge>{toolCalls.length}</Badge>
          {isStreaming && toolCalls.some(tc => !tc.result) && <Spinner />}
        </HeaderLeft>
        <ExpandIconWrapper>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </ExpandIconWrapper>
      </Header>

      <CollapsibleContent $expanded={expanded}>
        <ToolCallList>
          {toolCalls.map((tc, idx) => {
            const isItemExpanded = expandedItems.has(idx);
            const label = TOOL_LABELS[tc.toolName] || tc.toolName;
            const summary = getArgsSummary(tc.args);

            return (
              <ToolCallItem key={idx}>
                <ToolCallHeader $hasResult={!!tc.result} onClick={() => toggleItem(idx)}>
                  {tc.result !== undefined ? <ResultDot /> : <Spinner />}
                  <ToolName>{label}</ToolName>
                  <ToolArgsSummary>{summary}</ToolArgsSummary>
                  <ExpandIconWrapper>
                    {isItemExpanded ? <ExpandLessIcon style={{ fontSize: 16 }} /> : <ExpandMoreIcon style={{ fontSize: 16 }} />}
                  </ExpandIconWrapper>
                </ToolCallHeader>

                {isItemExpanded && (
                  <ToolCallBody>
                    <SectionLabel>Args</SectionLabel>
                    <CodeBlock>{JSON.stringify(tc.args, null, 2)}</CodeBlock>

                    {tc.result !== undefined && (
                      <>
                        <SectionLabel style={{ marginTop: 8 }}>
                          Result
                          {tc.truncated && <TruncatedBadge>(truncated)</TruncatedBadge>}
                        </SectionLabel>
                        <CodeBlock>{tc.result}</CodeBlock>
                      </>
                    )}
                  </ToolCallBody>
                )}
              </ToolCallItem>
            );
          })}
        </ToolCallList>
      </CollapsibleContent>
    </Container>
  );
};

export default ToolCallsDebugPanel;
